import React from 'react'

import {
  Checkbox,
  TableBody,
  TableFooter,
  TablePagination,
  useMediaQuery
} from '@mui/material'

import MobileCardList, {
  MobileCardsProp,
  resolveMobileCards
} from './MobileCardList'
import ScrollableTableContainer from './ScrollableTableContainer'
import SortableTableHead, {
  MultiSortConfig,
  SortableColumn,
  SortConfig
} from './SortableTableHead'
import { stickyEndCellSx } from './stickyCell'
import { HeadCell, Order, SelectParams } from './Table.interface'
import {
  Table as StyledTable,
  TableCell as EnhancedTableCell,
  TableRow as EnhancedTableRow
} from './Table.styles'
import type { TableDensity, TableFontSize } from './tableDisplay'

export interface EnhancedTableProps {
  /** Table rows data */
  rows?: any[]
  /** Table header configuration */
  header: HeadCell[]
  /** Current page (0-indexed) */
  page?: number
  /** Rows per page */
  rowsPerPage?: number
  /** Total count for pagination */
  totalCount?: number
  /** Page change handler */
  handleChangePage?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => void
  /** Rows per page change handler */
  handleChangeRowsPerPage?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  /** Enable row hover effect */
  hoverInRow?: boolean
  /** Row click handler */
  rowAction?: (uuid: string) => void
  /** Hide pagination */
  hiddePagination?: boolean
  /** Selection configuration */
  select: SelectParams
  /** Hide last row separator */
  hideLastRowSeparator?: boolean

  // === Server-side Sorting Props ===
  /** Current sort configuration (single or multi-column) */
  sortConfig?: SortConfig | MultiSortConfig
  /** Sort change handler - called with field and direction (single column) */
  onSort?: (field: string, direction: Order) => void
  /** Multi-column sort handler - receives full array of sorts */
  onMultiSort?: (sorts: MultiSortConfig) => void
  /** Enable multi-column sorting */
  multiSort?: boolean
  /** Configuration for which columns are sortable */
  sortableColumns?: SortableColumn[]
  /** Disable all sorting (client and server) */
  disableSort?: boolean

  // === Display preferences ===
  /** Per-table font size (sm | md | lg). */
  fontSize?: TableFontSize
  /** Per-table density (compact | comfortable | spacious). */
  density?: TableDensity
  /** Column ids pinned to the right edge while scrolling horizontally. */
  stickyEndColumns?: string[]

  // === Mobile ===
  /**
   * Ativa a versão em cards no mobile (<= 600px): em vez de uma tabela que
   * exige scroll lateral, cada linha vira um card com título, badge, pares
   * rótulo/valor e um rodapé de ações. `true` infere tudo do `header`; passe um
   * objeto para fixar qual coluna é título, badge ou ações. Sem a prop nada muda.
   */
  mobileCards?: MobileCardsProp
}

export const EnhancedTable: React.FC<EnhancedTableProps> = ({
  rows,
  header,
  page = 0,
  handleChangePage,
  handleChangeRowsPerPage,
  rowsPerPage = 25,
  totalCount = 0,
  hoverInRow = false,
  rowAction,
  hiddePagination = false,
  select,
  hideLastRowSeparator = false,
  // Server-side sorting
  sortConfig,
  onSort,
  onMultiSort,
  multiSort = false,
  sortableColumns = [],
  disableSort = false,
  fontSize,
  density,
  stickyEndColumns = [],
  mobileCards
}) => {
  const { useSelect, selected, setSelected } = select
  const isMobile = useMediaQuery('(max-width: 600px)', { noSsr: false })
  const stickyEndSet = new Set(stickyEndColumns)

  const isSelected = (uuid: string) => selected && selected.indexOf(uuid) !== -1

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows && rows.map((n) => n.uuid)
      !!setSelected &&
        setSelected((prevState: any) => ({
          ...prevState,
          selected: newSelected
        }))
      return
    }
    !!setSelected &&
      setSelected((prevState) => ({ ...prevState, selected: [] }))
  }

  const handleClick = (uuid: string) => {
    if (selected) {
      const selectedIndex = selected.indexOf(uuid)
      let newSelected: string[] = []

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, uuid)
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1))
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1))
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        )
      }

      !!setSelected &&
        setSelected((prevState) => ({ ...prevState, selected: newSelected }))
    }
  }

  const mobileCardsConfig = resolveMobileCards(mobileCards, header)

  if (mobileCardsConfig && isMobile) {
    return (
      <MobileCardList
        rows={rows}
        config={mobileCardsConfig}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        handleChangePage={handleChangePage}
        hiddePagination={hiddePagination}
        select={select}
        rowAction={rowAction}
        sortableColumns={sortableColumns}
        sortConfig={sortConfig}
        onSort={onSort}
        onMultiSort={onMultiSort}
        disableSort={disableSort}
      />
    )
  }

  return (
    <ScrollableTableContainer fontSize={fontSize} density={density}>
      <StyledTable>
        <SortableTableHead
          header={header}
          sortConfig={sortConfig}
          onSort={onSort}
          onMultiSort={onMultiSort}
          multiSort={multiSort}
          sortableColumns={sortableColumns}
          useSelect={useSelect}
          numSelected={(selected && selected.length) || 0}
          rowCount={(rows && rows.length) || 0}
          onSelectAllClick={handleSelectAllClick}
          disableSort={disableSort}
          stickyEndColumns={stickyEndColumns}
        />
        <TableBody>
          {(rows || []).map(
            ({ uuid, backgroundColor, clickable, ...row }, index) => {
              const isItemSelected = isSelected(uuid as string)
              const isLastRow = index === (rows?.length || 0) - 1
              // Without an explicit flag, only rows that do something on click get the hand cursor
              const isRowClickable =
                typeof clickable === 'boolean'
                  ? clickable
                  : !!rowAction || !!useSelect
              // Hover highlight follows hoverInRow; an explicit clickable: false still turns it off
              const showRowHover = hoverInRow && clickable !== false

              return (
                <EnhancedTableRow
                  sx={{
                    backgroundColor: backgroundColor ?? ''
                  }}
                  key={uuid || index}
                  hoverInRow={showRowHover}
                  isLastRow={isLastRow}
                  hideLastRowSeparator={hideLastRowSeparator}
                  {...(useSelect && {
                    onClick: () => handleClick(uuid as string)
                  })}
                >
                  {useSelect && (
                    <EnhancedTableCell component="th" scope="row">
                      <Checkbox color="primary" checked={isItemSelected} />
                    </EnhancedTableCell>
                  )}
                  {Object.keys(row).map((key, i) => (
                    <EnhancedTableCell
                      component="th"
                      key={String(i)}
                      scope="row"
                      clickable={isRowClickable}
                      {...(stickyEndSet.has(key) && {
                        sx: stickyEndCellSx('body')
                      })}
                      {...(rowAction &&
                        isRowClickable && {
                          onClick: () => rowAction(String(uuid))
                        })}
                    >
                      {(() => {
                        const value = row[key]
                        return value &&
                          typeof value === 'object' &&
                          'component' in value
                          ? (value as any).component
                          : value
                      })()}
                    </EnhancedTableCell>
                  ))}
                </EnhancedTableRow>
              )
            }
          )}
        </TableBody>
        {!hiddePagination && (
          <TableFooter>
            <TablePagination
              count={totalCount}
              labelDisplayedRows={({ count, to, from }) =>
                `${from}–${to} ${
                  count !== -1 ? `de ${count}` : `mais que ${to}`
                }`
              }
              labelRowsPerPage="Registros por página:"
              onPageChange={(event, newPage) =>
                !!handleChangePage && handleChangePage(event, newPage)
              }
              onRowsPerPageChange={(event) =>
                !!handleChangeRowsPerPage && handleChangeRowsPerPage(event)
              }
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </TableFooter>
        )}
      </StyledTable>
    </ScrollableTableContainer>
  )
}

export default EnhancedTable
